// src/Components/Index.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Row, Col, Button, Offcanvas } from "react-bootstrap";
import CatalogCollection from "Pages/Catalog/CatalogCollection";
import Filters from "Pages/Catalog/Filters";

import {
  useListStoreProductsPageQuery,
  type StoreProductCardOut,
  type StoreProductAvailableFiltersOut,
} from "../redux/services/store/storeProductsApi";
import fallbackImg from "assets/images/products/img-1.png";

type ApiArgs = {
  skip?: number;
  limit?: number;
  q?: string | null;
  category_id?: number | null;
  category_slug?: string | null;
  brand_id?: number | null;
  is_featured?: boolean | null;
  price_min?: number | null;
  price_max?: number | null;
  discount_min?: number | null;
  attribute_value_ids?: number[] | null;
  sort?: string | null;
};

type SelectedFilters = {
  q?: string;
  brand_id?: number | null;
  price_min?: number | null;
  price_max?: number | null;
  discount_min?: number | null;
  attributes?: Record<string, number[]>;
};

const emptyAvailableFilters: StoreProductAvailableFiltersOut = {
  brands: [],
  attributes: [],
  price: null,
  stock: { in_stock: 0, out_of_stock: 0 },
  discount_ranges: [],
};

const COLOR_ATTRIBUTE_CODES = new Set(["color", "colour"]);

const normalizeFilterToken = (value: unknown): string =>
  String(value ?? "").trim().toLowerCase();

const isColorAttributeGroup = (group: {
  code?: string | null;
  display_layout?: string | null;
}) =>
  group.display_layout === "color_swatch" ||
  COLOR_ATTRIBUTE_CODES.has(normalizeFilterToken(group.code));

const PRODUCTS_PER_PAGE = 50;

const createDefaultSelectedFilters = (q = ""): SelectedFilters => ({
  q,
  brand_id: null,
  price_min: null,
  price_max: null,
  discount_min: null,
  attributes: {},
});

const createDefaultApiArgs = (q: string | null = null): ApiArgs => ({
  skip: 0,
  limit: PRODUCTS_PER_PAGE,
  q,
  brand_id: null,
  is_featured: null,
  price_min: null,
  price_max: null,
  discount_min: null,
  attribute_value_ids: null,
  sort: null,
});

const Index = ({
  name,
  cxxl,
  clg,
  cmd,
  cxl,
  category_id,
  category_slug,
  disableRouteCategoryFilter,
}: any) => {
  const location = useLocation();
  const params = useParams();
  const urlSlug = ((params as any)?.categorySlug ??
    (params as any)?.slug) as string | undefined;
  const routeSearchQuery = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return (searchParams.get("q") ?? "").trim();
  }, [location.search]);

  const effectiveCategorySlug = disableRouteCategoryFilter
    ? category_slug ?? null
    : category_slug ?? urlSlug ?? null;
  const effectiveCategoryId = category_id ?? null;

  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(
    () => createDefaultSelectedFilters(routeSearchQuery)
  );

  const [apiArgs, setApiArgs] = useState<ApiArgs>(() =>
    createDefaultApiArgs(routeSearchQuery || null)
  );

  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const productsPanelRef = useRef<HTMLDivElement | null>(null);
  const productsTopRef = useRef<HTMLDivElement | null>(null);

  const scrollProductsToTop = () => {
    const panel = productsPanelRef.current;
    if (panel) {
      try {
        panel.scrollTo({ top: 0, behavior: "smooth" });
      } catch {
        panel.scrollTop = 0;
      }
    }

    if (typeof window !== "undefined" && window.innerWidth < 768) {
      const anchorTop = productsTopRef.current?.getBoundingClientRect().top;
      if (typeof anchorTop === "number") {
        const absoluteTop = anchorTop + window.scrollY - 96;
        window.scrollTo({ top: Math.max(0, absoluteTop), behavior: "smooth" });
      }
    }
  };

  const handlePageChange = (nextPage: number) => {
    const safePage = Math.max(1, Math.min(totalPages, nextPage));
    scrollProductsToTop();
    setCurrentPage(safePage);
  };

  useEffect(() => {
    setSelectedFilters(createDefaultSelectedFilters(routeSearchQuery));
    setApiArgs(createDefaultApiArgs(routeSearchQuery || null));

    setCurrentPage(1);
    setShowMobileFilters(false);
  }, [effectiveCategorySlug, effectiveCategoryId, routeSearchQuery]);

  const finalArgs: ApiArgs = {
    ...apiArgs,
    skip: (currentPage - 1) * PRODUCTS_PER_PAGE,
    limit: PRODUCTS_PER_PAGE,
    category_id: effectiveCategoryId,
    category_slug: effectiveCategorySlug,
  };

  const { data, isLoading, isFetching, isError } =
    useListStoreProductsPageQuery(finalArgs as any);

  const availableFilters = data?.available_filters ?? emptyAvailableFilters;
  const selectedColorFilters = useMemo(() => {
    const selections: Array<{ code: string; label: string }> = [];
    const selectedAttributes = selectedFilters.attributes ?? {};

    for (const group of availableFilters.attributes ?? []) {
      if (!isColorAttributeGroup(group)) continue;

      const selectedIds = selectedAttributes[group.code] ?? [];
      if (!Array.isArray(selectedIds) || selectedIds.length === 0) continue;

      const valuesById = new Map(
        (group.values ?? []).map((value) => [value.id, value])
      );

      for (const valueId of selectedIds) {
        const value = valuesById.get(valueId);
        if (!value) continue;

        const code = normalizeFilterToken(value.value_code);
        const label = normalizeFilterToken(value.label);
        if (!code && !label) continue;

        selections.push({ code, label });
      }
    }

    return selections;
  }, [availableFilters, selectedFilters.attributes]);

  const hasActiveFilters = useMemo(() => {
    const attrs = selectedFilters.attributes ?? {};
    const hasAttrs = Object.values(attrs).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    );

    return Boolean(
      (selectedFilters.q ?? "").trim() ||
        selectedFilters.brand_id !== null ||
        selectedFilters.price_min !== null ||
        selectedFilters.price_max !== null ||
        selectedFilters.discount_min !== null ||
        hasAttrs
    );
  }, [selectedFilters]);

  const handleChangeFilters = (next: SelectedFilters) => {
    setSelectedFilters(next);

    const flatAttributeValueIds = Object.values(next.attributes ?? {})
      .flat()
      .filter((x): x is number => typeof x === "number" && Number.isFinite(x));

    setApiArgs((prev) => ({
      ...prev,
      skip: 0,
      q: next.q?.trim() ? next.q.trim() : null,
      brand_id: next.brand_id ?? null,
      price_min: next.price_min ?? null,
      price_max: next.price_max ?? null,
      discount_min: next.discount_min ?? null,
      attribute_value_ids: flatAttributeValueIds.length
        ? flatAttributeValueIds
        : null,
    }));
    handlePageChange(1);
  };

  const handleSortChange = (value: string) => {
    const nextSort =
      value === "lowtohigh"
        ? "price_asc"
        : value === "hightolow"
          ? "price_desc"
          : null;

    setApiArgs((prev) => ({
      ...prev,
      skip: 0,
      sort: nextSort,
    }));
    handlePageChange(1);
  };

  const totalProducts = Math.max(0, Number(data?.total ?? 0));
  const totalPages = Math.max(1, Math.ceil(totalProducts / PRODUCTS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const apiListForUI = useMemo(() => {
    const items: StoreProductCardOut[] = data?.items ?? [];

    return items.map((p) => {
      let img = p?.image?.cdn_url || fallbackImg;
      let imgAlt = p?.image?.alt_text || p?.name || "";
      if (selectedColorFilters.length > 0) {
        for (const selectedColor of selectedColorFilters) {
          const preview = (p.color_variant_images ?? []).find((item) => {
            const previewCode = normalizeFilterToken(item?.value_code);
            const previewLabel = normalizeFilterToken(item?.label);

            return (
              (selectedColor.code && previewCode === selectedColor.code) ||
              (selectedColor.label && previewLabel === selectedColor.label)
            );
          });

          if (preview?.image?.cdn_url) {
            img = preview.image.cdn_url;
            imgAlt = preview.image.alt_text || imgAlt;
            break;
          }
        }
      }

      const priceStr = p?.price_from ?? "0";
      const compareStr = p?.compare_at_from ?? "";

      let discountPct = 0;
      if (compareStr) {
        const a = Number(priceStr);
        const b = Number(compareStr);
        if (b > 0 && a > 0 && b > a) {
          discountPct = Math.round(((b - a) / b) * 100);
        }
      }

      return {
        id: p.id,
        slug: p.slug,
        img,
        imgAlt,
        title: p.name,
        ratting: "0",
        price: `${p?.currency_code || "INR"} ${priceStr}`,
        deleteproce: compareStr ? `${p?.currency_code || "INR"} ${compareStr}` : "",
        presentag: discountPct > 0 ? `${discountPct}%` : "",
        dic: discountPct,
        stock: p.in_stock === false ? "Out of stock" : "In stock",
        bg: p.in_stock === false ? "danger" : "success",
        inStock: p.in_stock !== false,
        productType: p.product_type,
        cardVariantId: p.card_variant_id ?? null,
        canDirectAddToCart: Boolean(p.can_direct_add_to_cart),
        isCustomizable: Boolean(p.is_customizable),
        color: [],
        size: [],
        icone: "ri-image-line",
      };
    });
  }, [data, selectedColorFilters]);

  return (
    <React.Fragment>
      <style>
        {`
          .category-page-layout {
            min-height: 100%;
          }

          .category-filter-panel,
          .category-products-panel {
            min-height: 0;
          }

          .mobile-filter-toolbar {
            display: none;
          }

          .mobile-filter-trigger {
            min-height: 44px;
            border-radius: 999px !important;
            padding: 10px 16px !important;
            font-weight: 600 !important;
            display: inline-flex !important;
            align-items: center;
            gap: 8px;
          }

          .mobile-filter-count-badge {
            min-width: 18px;
            height: 18px;
            padding: 0 6px;
            border-radius: 999px;
            background: #111;
            color: #fff;
            font-size: 11px;
            font-weight: 700;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            line-height: 1;
          }

          .mobile-filter-offcanvas .offcanvas-header {
            border-bottom: 1px solid rgba(0, 0, 0, 0.08);
            padding: 14px 16px;
          }

          .mobile-filter-offcanvas .offcanvas-title {
            font-size: 16px;
            font-weight: 700;
            color: #111827;
          }

          .mobile-filter-offcanvas .offcanvas-body {
            padding: 0;
            overflow-y: auto;
          }

          .mobile-filter-content {
            padding: 16px;
          }

          .mobile-filter-content .card {
            border: 0 !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            margin-bottom: 0;
          }

          .mobile-filter-content .card-header {
            background: #fff;
            padding-left: 0;
            padding-right: 0;
            padding-top: 0;
          }

          .mobile-filter-content .accordion-body {
            padding-left: 0;
            padding-right: 0;
          }

          .mobile-filter-content .accordion-button {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }

          @media (min-width: 768px) {
            .category-page-layout.desktop-split-scroll {
              height: calc(100vh - 110px);
              overflow: hidden;
            }

            .category-page-layout.desktop-split-scroll .category-row {
              height: 100%;
              align-items: flex-start;
            }

            .category-page-layout.desktop-split-scroll .category-filter-col,
            .category-page-layout.desktop-split-scroll .category-products-col {
              height: 100%;
            }

            .category-page-layout.desktop-split-scroll .category-filter-panel,
            .category-page-layout.desktop-split-scroll .category-products-panel {
              height: 100%;
              overflow-y: auto;
              overflow-x: hidden;
              padding-right: 6px;
              -webkit-overflow-scrolling: touch;
            }
          }

          @media (max-width: 767.98px) {
            .category-page-layout.mobile-normal-flow {
              height: auto;
              overflow: visible;
            }

            .category-page-layout.mobile-normal-flow .category-row {
              height: auto;
            }

            .category-page-layout.mobile-normal-flow .category-products-col {
              height: auto;
            }

            .category-page-layout.mobile-normal-flow .category-products-panel {
              height: auto;
              overflow: visible;
              padding-right: 0;
            }

            .mobile-filter-toolbar {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              margin-bottom: 16px;
              flex-wrap: wrap;
            }

            .mobile-filter-toolbar-text {
              font-size: 13px;
              color: #6b7280;
              font-weight: 500;
            }
          }
        `}
      </style>

      <div className="category-page-layout desktop-split-scroll mobile-normal-flow">
        <Row className="g-4 category-row">
          <Col
            xl={3}
            lg={4}
            md={5}
            className="category-filter-col d-none d-md-block"
          >
            <div className="category-filter-panel">
              <Filters
                name={name}
                availableFilters={availableFilters}
                selectedFilters={selectedFilters}
                onChangeFilters={handleChangeFilters}
              />
            </div>
          </Col>

          <Col
            xl={9}
            lg={8}
            md={7}
            xs={12}
            className="category-products-col"
          >
            <div className="category-products-panel" ref={productsPanelRef}>
              <div ref={productsTopRef} />
              <div className="mobile-filter-toolbar d-md-none">
                <div className="mobile-filter-toolbar-text">
                  {apiListForUI.length} products
                </div>

                <Button
                  variant={hasActiveFilters ? "dark" : "outline-dark"}
                  className="mobile-filter-trigger"
                  onClick={() => setShowMobileFilters(true)}
                >
                  <i className="ri-equalizer-line"></i>
                  <span>Filter</span>
                  {hasActiveFilters ? (
                    <span className="mobile-filter-count-badge">•</span>
                  ) : null}
                </Button>
              </div>

              <CatalogCollection
                filterList={apiListForUI}
                cxxl={cxxl}
                clg={clg}
                cmd={cmd}
                cxl={cxl}
                currentPage={currentPage}
                perPageData={PRODUCTS_PER_PAGE}
                totalResults={totalProducts}
                sortValue={
                  apiArgs.sort === "price_asc"
                    ? "lowtohigh"
                    : apiArgs.sort === "price_desc"
                      ? "hightolow"
                      : "all"
                }
                onSortChange={handleSortChange}
                onPageChange={handlePageChange}
                isLoading={isLoading || isFetching}
                isError={isError}
              />
            </div>
          </Col>
        </Row>
      </div>

      <Offcanvas
        show={showMobileFilters}
        onHide={() => setShowMobileFilters(false)}
        placement="end"
        className="mobile-filter-offcanvas d-md-none"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            Filters {hasActiveFilters ? "(Applied)" : ""}
          </Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <div className="mobile-filter-content">
            <Filters
              name={name}
              availableFilters={availableFilters}
              selectedFilters={selectedFilters}
              onChangeFilters={handleChangeFilters}
            />

            <div className="pt-3">
              <Button
                variant="dark"
                className="w-100 rounded-pill"
                onClick={() => setShowMobileFilters(false)}
              >
                View Products
              </Button>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </React.Fragment>
  );
};

export default Index;
